import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { AppKey } from '@qms360/shared';
import { fetchUnreadCount } from '../lib/notifications-api.js';

/**
 * The bell (spec §13.6). Reminders, breaches and escalations were recorded
 * for months with nowhere to see them (KNOWN_ISSUES FN-12); this is where they
 * surface. The count refreshes every minute and on focus — a count that lags a
 * little is fine; one that never moves is how people stop looking.
 */
const REFRESH_MS = 60_000;

export function NotificationBell({ app }: { app: AppKey }) {
  const { data } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: REFRESH_MS,
    retry: false,
  });
  const unread = data?.unread ?? 0;
  const label = unread > 0 ? `Notifications, ${unread} unread` : 'Notifications';

  return (
    <Link
      to={`/${app}/notifications`}
      aria-label={label}
      title={label}
      className="relative rounded-sm px-2 py-1.5 text-white/80 no-underline hover:bg-white/10 hover:text-white"
    >
      <svg aria-hidden width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
      {unread > 0 && (
        <span
          data-testid="unread-badge"
          className="absolute -end-1 -top-1 min-w-[1.25rem] rounded-full bg-ag-fuchsia px-1 text-center text-xs font-semibold leading-5 text-white"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  );
}
